// Polling hook for an active workflow run. Used by WorkflowEditor to show
// node states + run details inline (no navigation away from the editor).
//
// Polling is the baseline transport. The SSE stream at
// /v1/runs/:id/events/stream is layered on top via
// `useRunEventStreamInvalidation`, which invalidates these queries on
// each event for near-realtime updates. Polling at ~1.5s remains as the
// fallback when EventSource construction fails or the connection drops
// — belt-and-suspenders so the canvas's run-state visuals never freeze
// even on flaky networks. (DEV-16156 / DEV-16173.)
import { useClient } from '@cortex/react-query';
import type { Run } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { terminalRunStatuses, type RunStatusKey } from '../../lib/constants';

export interface INodeExecution {
  id: string;
  runId: string;
  nodeKey: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'SKIPPED' | 'PAUSED';
  startedAt?: string | null;
  finishedAt?: string | null;
  durationMs?: number | null;
  costUsd?: number | null;
  output?: Record<string, unknown> | null;
  error?: Record<string, unknown> | string | null;
}

export interface ILiveRunState {
  run: Run | null;
  nodeExecutions: INodeExecution[];
  /** nodeKey → status. Pre-computed for the editor's node renderer. */
  statusByNodeKey: Record<string, INodeExecution['status']>;
  /**
   * nodeKey → executed duration in ms. Only populated when the API has
   * stamped `durationMs` on the execution (typically only after the node
   * reaches a terminal status); RUNNING / PENDING nodes are absent.
   */
  durationByNodeKey: Record<string, number>;
  isTerminal: boolean;
  isLoading: boolean;
  error: Error | null;
}

const pollMs = 1500;

/**
 * Polls /v1/runs/:id and /v1/runs/:id/node-executions every ~1.5s while the
 * run is non-terminal. Stops polling when status flips to a terminal value.
 */
export function useLiveRun(runId: string | null): ILiveRunState {
  const client = useClient();

  const runQuery = useQuery({
    enabled: Boolean(runId),
    queryKey: ['live-run', runId],
    queryFn: () => client.http.get(`v1/runs/${runId}`).json<Run>(),
    refetchInterval: (q) => {
      const data = q.state.data as Run | undefined;
      if (!data) return pollMs;
      return terminalRunStatuses.has(data.status as RunStatusKey) ? false : pollMs;
    },
  });

  const nodeExecQuery = useQuery({
    enabled: Boolean(runId),
    queryKey: ['live-run', runId, 'node-executions'],
    queryFn: () =>
      client.http
        .get(`v1/runs/${runId}/node-executions?limit=200`)
        .json<{ items: INodeExecution[]; total: number } | INodeExecution[]>()
        .then((r) => (Array.isArray(r) ? r : (r.items ?? []))),
    refetchInterval: (q) => {
      // Keep polling node-executions while the run is non-terminal AND for
      // one extra tick after — the runner flushes status writes async, so
      // the final node may flip to COMPLETED a beat after the run does.
      const run = runQuery.data;
      if (!run) return pollMs;
      const isTerminal = terminalRunStatuses.has(run.status as RunStatusKey);
      if (!isTerminal) return pollMs;
      // One extra refetch on terminal — q.state.dataUpdateCount tells us
      // whether we've already polled at least once after terminal.
      return q.state.dataUpdateCount > 0 ? false : pollMs;
    },
  });

  const run = runQuery.data ?? null;
  // Memo'd so the array identity is stable when the underlying query hasn't
  // refetched — otherwise the `?? []` fallback would mint a fresh literal
  // each render, blowing the cache on every memo that consumes this.
  const nodeExecutions = useMemo(() => nodeExecQuery.data ?? [], [nodeExecQuery.data]);

  // nodeKey → status / duration. Memo'd alongside `nodeExecutions` so the
  // returned map identities stay stable across renders that don't touch the
  // live run — the editor's `decoratedNodes` useMemo lists both maps in its
  // deps, and unmemoized object literals here would defeat its cache.
  // If a node is listed multiple times (rare; the API tracks one execution
  // per nodeKey per run today), the latest entry wins.
  const { statusByNodeKey, durationByNodeKey } = useMemo(() => {
    const status: Record<string, INodeExecution['status']> = {};
    const duration: Record<string, number> = {};
    for (const ne of nodeExecutions) {
      status[ne.nodeKey] = ne.status;
      if (typeof ne.durationMs === 'number' && Number.isFinite(ne.durationMs)) {
        duration[ne.nodeKey] = ne.durationMs;
      }
    }
    return { statusByNodeKey: status, durationByNodeKey: duration };
  }, [nodeExecutions]);

  const isTerminal = run ? terminalRunStatuses.has(run.status as RunStatusKey) : false;

  return {
    run,
    nodeExecutions,
    statusByNodeKey,
    durationByNodeKey,
    isTerminal,
    isLoading: runQuery.isLoading || nodeExecQuery.isLoading,
    error: (runQuery.error as Error | null) ?? (nodeExecQuery.error as Error | null),
  };
}
